import { useCallback, useRef, useState } from "react";
import Webcam from "react-webcam";

export const useVideoCapture = (fileNamePrefix: string = '', count: number = 0) => {
    const webcamRef = useRef<Webcam | null>(null);
    const intervalRef = useRef<NodeJS.Timer | null>(null);
    const [capturing, setCapturing] = useState(false);
    const [streamFile, setStreamFile] = useState<File | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
    const streamCountRef = useRef<number>(count);

    const setStreamCount = (count: number) => {
        streamCountRef.current = count;
    };

    const startCapture = () => {
        if (webcamRef.current?.stream) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            let mimeType = '';
            if (MediaRecorder.isTypeSupported('video/webm')) {
                mimeType = 'video/webm;codecs=vp8,opus';
            } else if (MediaRecorder.isTypeSupported('video/mp4')) {
                mimeType = 'video/mp4';
            }

            setCapturing(true);
            // streamCountRef.current += 1;
            mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
                mimeType,
                videoBitsPerSecond: 2500000,  // Adjust as needed
            });

            mediaRecorderRef.current.addEventListener("dataavailable", handleDataAvailable);
            mediaRecorderRef.current.start();
        } else {
            intervalRef.current = setInterval(() => {
                if (webcamRef.current && webcamRef.current.stream) {
                    startCapture();
                }
            }, 100);
        }
    };

    const handleDataAvailable = useCallback(({ data }: BlobEvent) => {
        if (data.size > 0) {
            const file = new File([data], `stream_${streamCountRef.current}.webm`, { type: "video/webm" });
            setStreamFile(file);
            streamCountRef.current += 1;
        }
    }, [setRecordedChunks]);

    const stopCapture = () => {
        mediaRecorderRef.current?.stop();
        setCapturing(false);
    };

    const download = useCallback(() => {
        if (recordedChunks.length) {
            const blob = new Blob(recordedChunks, {
                type: "video/webm"
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.href = url;
            a.download = "webcam-stream.webm";
            a.click();
            window.URL.revokeObjectURL(url);
            setRecordedChunks([]);
        }
    }, [recordedChunks]);

    return {
        capturing,
        webcamRef,
        streamFile,
        download,
        stopCapture,
        startCapture,
        setStreamCount,
        chunkNumber: streamCountRef.current,
        streamFileName: `${fileNamePrefix ? `${fileNamePrefix}_` : ''}stream_${streamCountRef.current}.webm`,
    }
};